import {CheckOutlined} from '@ant-design/icons';
import {Button, Divider} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {toast} from 'react-hot-toast';
import {BsLightningChargeFill} from 'react-icons/bs';
import {MdAddShoppingCart} from 'react-icons/md';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {addToCart} from '../../../redux/authAction';
import {LoginScreen} from '../../LoginScreen';

import ProductDetailInfo from './ProductDetailInfo/ProductDetailInfo';

const ProductInformation = ({data, selectedColor, setSelectedColor}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector(state => state.cart);
  const {loggedIn} = useSelector(state => state.auth);

  const [count, setCount] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [sizeError, setSizeError] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const variants = data?.variants || [];

  const selectedVariant = variants.find(v => v.color === selectedColor) || {};

  const sizes = selectedVariant?.sizes || [];

  const validSizes = Array.isArray(sizes)
    ? sizes.filter(s => s.size && s.size.trim() !== '')
    : [];

  const hasSizes = validSizes.length > 0;
  const colorLevelQty = hasSizes
    ? Math.max(...validSizes.map(s => Number(s.quantity || 0)))
    : Number(selectedVariant?.quantity || data?.quantity || 0);

  const normalizedStatus = (data?.status || '').toUpperCase();

  const isProductInactive =
  normalizedStatus !== 'ACTIVE' &&
  Number(selectedVariant?.quantity || data?.quantity || 0) < 1;
  
  useEffect(() => {
    setCount(1);
    setSelectedSize(null);
    setSizeError(false);
  }, [data]); 
  useEffect(() => {
    setSelectedSize(null);
    setCount(1);
  }, [selectedColor]);

  const selectedVariantImage = selectedVariant?.images?.[0] || '';

  /* Size select */
  const handleSizeSelect = sizeObj => {
    if (selectedSize?.size === sizeObj.size) {
      setSelectedSize(null);
      setCount(1);
    } else {
      setSelectedSize(sizeObj);
      setSizeError(false);
      setCount(1);
    }
  };

  const getAvailableQty = () => {
    if (hasSizes) {
      return Number(selectedSize?.quantity || 0);
    }

    return Number(
      sizes?.[0]?.quantity ||   // fallback
    selectedVariant?.quantity ||
    data?.quantity ||
    0,
    );
  };

  const isNoSizeOutOfStock = !hasSizes && getAvailableQty() === 0;

  /* Quantity */
  const increase = () => {
    if (isProductInactive) {return;}

    if (hasSizes && !selectedSize) {
      setSizeError(true);
      return;
    }

    const availableQty = getAvailableQty();

    if (availableQty <= 0) {
      toast.error('Out of stock');
      return;
    }

    if (count >= availableQty) {
      toast.error(`Only ${availableQty} items available`);
      return;
    }

    setCount(prev => prev + 1);
  };
  const decrease = () => {
    if (isProductInactive) {return;}
    setCount(prev => (prev > 1 ? prev - 1 : 1));
  };

  /* Price calculation */
  const basePrice = Math.round(
    Number(data.price) - (Number(data.price) * data.discount) / 100,
  );

  const finalPrice =
  basePrice + (hasSizes ? selectedSize?.extraPrice || 0 : 0);
  /* Cart Buy */
  const handleClickButton = type => {
    if (isProductInactive) {
      toast.error('Product is not available in stock.');
      return;
    }
    if (!loggedIn) {
      toast.error('Please login before placing your order.');
      setShowLoginModal(true);
      return;
    }
    if (hasSizes  && !selectedSize) {
      setSizeError(true);
      return;
    }

    const existingCartItem = cart.items.find(item =>
      item.productId === data.id &&
  item.color === selectedColor &&
  (
    hasSizes
      ? item.size === selectedSize?.size
      : true
  ),
    );

    if (
      hasSizes &&
     existingCartItem &&
     selectedSize &&
  existingCartItem.quantity + count > selectedSize.quantity
    ) {
      toast.error(
        `Only ${selectedSize.quantity} items available for ${selectedColor} - ${selectedSize.size}`,
      );
      return;
    }
    const promoInfo = data.promocodeInfo || null;
    const productData = {
      productId: data.id,
      productName: data.productName,
      productType: data.productType,
      color: selectedColor,
      size: hasSizes ? selectedSize?.size : null,
      extraPrice: hasSizes ? selectedSize?.extraPrice : 0,
      quantity: count,
      pricePerUnit: finalPrice,
      discount: data.discount,
      imageUrl: selectedVariantImage,
      description: data.description,
      promoCode: promoInfo?.code || null,
      promocodeInfo: promoInfo,
      totalAmount: finalPrice * count,
    };

    if (type === 'fromBuy') {
      navigate('/checkout', {state: {product: productData}});
    } else {
      dispatch(addToCart(productData));
      navigate('/cart');
    }

  };
  useEffect(() => {
    const maxQty = getAvailableQty();

    if (count > maxQty && maxQty > 0) {
      setCount(maxQty);
    }
  }, [selectedSize, selectedColor, data]);

  {getAvailableQty() === 0 && (
    <p className={'text-red-500 text-sm'}>
    Out of stock
    </p>
  );}

  return (
    <div
      className={'flex flex-col md:gap-3 gap-1 p-5 shadow-md rounded-lg w-full'}
    >
      <h1 className={'md:text-4xl text-2xl font-serif font-bold'}>
        {data.productName}
      </h1>

      {/* PRICE */}
      <div className={'flex md:gap-4 mt-3 gap-2 items-center'}>
        <p className={'md:text-3xl text-xl font-semibold'}>₹{finalPrice}</p>
        {data.discount > 0 && (
          <p className={'line-through md:text-xl text-base text-gray'}>
            ₹{data.price}
          </p>
        )}

        {data.discount > 0 && (
          <span
            className={'bg-red-600 px-2 rounded-full text-red-50 font-semibold'}
          >
            -{data.discount}%
          </span>
        )}
        
      </div>

      <p className={'text-gray font-medium'}>Price inclusive of all taxes
      </p>
      <Divider className={'my-2 bg-primary-50'} />

      {/* COLORS */}
      <div className={'mb-2'}>
        <div className={'md:font-semibold font-bold mb-2'}>
    Select Colour
        </div>

        <div className={'flex gap-3 flex-wrap'}>
          {variants.map((v, index) => {
            
            const getImageUrl = url => {
              if (!url) {return '/no-image.png';}
              return url.replace('dl=0', 'raw=1');
            };
            const isSelected = selectedColor === v.color;
            const image = getImageUrl(v.images?.[0]);

            return (
              <button
                key={index}
                onClick={() => setSelectedColor(v.color)}
                className={`relative rounded-md border-2 p-[2px] transition ${
                  isSelected ? 'border-primary-900' : 'border-primary-50'
                }`}
              >
                <img
                  src={image}
                  alt={v.color}
                  className={'w-14 h-16 object-cover rounded-md'}
                />

                {isSelected && (
                  <div className={'absolute inset-0 flex items-center justify-center rounded-md'}>
                    {/* <CheckOutlined style={{color: '#fff', fontSize: 16}} /> */}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* SIZES */}
      {hasSizes && (
        <div className={'mb-2'}>
          <div className={'md:font-semibold font-bold mb-0'}>Choose Size</div>

          <div className={'flex gap-2 flex-wrap'}>
            {sizes
              .filter(s => s.size && s.size.trim() !== '')
              .map(s => {
                const isOutOfStock = Number(s.quantity) === 0;

                return (
                  <div key={s.size} className={'p-2 rounded-md'}>
                    <Button
                      disabled={isProductInactive || isOutOfStock}
                      onClick={() => handleSizeSelect(s)}
                      className={
                        selectedSize?.size === s.size
                          ? '!border-primary-700 !bg-primary-700 !text-white'
                          : isOutOfStock
                            ? '!border-gray-300 !text-gray-400 cursor-not-allowed'
                            : '!border-gray'
                      }
                    >
                      <p>{s.size}</p>
                    </Button>

                    {/* PRICE */}
                    {!isOutOfStock && (
                      <p className={'font-extralight mt-1 pl-1'}>
              ₹{basePrice + s.extraPrice}
                      </p>
                    )}

                    {/* OUT OF STOCK MESSAGE */}
                    {isOutOfStock && (
                      <>
                        <div className={'w-full h-[1px] bg-gray-300 my-1'}></div>
                        <p className={'text-xs text-red-500 text-center'}>
                Currently <br /> unavailable
                        </p>
                      </>
                    )}
                  </div>
                );
              })}
              
          </div>
          {sizeError && (
            <p className={'text-red-500 text-sm mt-1'}>
        Please select a size
            </p>
          )}
          
        </div>
      )}

      {/* No size and out of stock */}
      {isNoSizeOutOfStock && (
        <div className={'text-red-600 text-sm font-semibold mt-2'}>
    Selected colour currently not available
        </div>
      )}

      {/* PRODUCT INACTIVE */}
      {isProductInactive && (
        <div className={' text-red-600 p-3 text-lg rounded-md text-center font-semibold mt-3'}>
    Currently Not Available
        </div>
      )}

      {/* ACTIONS */}
      <div className={'flex md:flex-row flex-col md:gap-4 gap-2  mt-3'}>
        <div
          className={
            'flex md:mr-2 items-center md:w-44 w-20 md:mb-0 mb-2 rounded-full bg-primary-50 md:gap-3 gap-1'
          }
          disabled={isProductInactive}
        >
          <Button
            className={
              'border-none rounded-full bg-primary-50 hover:!bg-primary-50 font-semibold md:text-lg'
            }
            disabled={isProductInactive}
            onClick={decrease}
          >
            -
          </Button>
          <span className={'font-bold'}>{count}</span>
          <Button
            className={`border-none rounded-full font-semibold md:text-lg
    ${
    isProductInactive || getAvailableQty() <= 0
      ? '!bg-primary-50 !text-gray-400 cursor-not-allowed'
      : '!bg-primary-50 hover:!bg-primary-50'
    }
  `}
            // disabled if product inactive and no size , no quantity 
            disabled={isProductInactive || getAvailableQty() <= 0}
            onClick={() => {
              if (count >= getAvailableQty()) {
                toast.error(`Only ${getAvailableQty()} items available`);
                return;
              }
              increase();
            }}
          >
  +
          </Button>
        </div>

        <div className={'flex w-full gap-3'}>
          <Button
            onClick={() => handleClickButton('fromBuy')}
            // disabled if product inactive and no size , no quantity 
            disabled={isProductInactive || isNoSizeOutOfStock}
            className={` ${isProductInactive || isNoSizeOutOfStock ? ' p-3 md:p-5 md:py-4 py-5 text-white rounded-full hover:!bg-slate-100 w-full':'bg-primary-900 hover:!bg-primary-800 hover:!text-primary-50 p-3 md:p-5 md:py-4 py-5 text-white rounded-full w-full'}`}
          >
            <BsLightningChargeFill  /> Buy Now
          </Button>
          <Button
            onClick={() => handleClickButton('fromCart')}
            disabled={isProductInactive || isNoSizeOutOfStock}
            className={` ${isProductInactive || isNoSizeOutOfStock ? ' p-3 md:p-5 md:py-4 hover:!bg-slate-100 py-5 text-white rounded-full w-full':'bg-primary-900 hover:!bg-primary-800 hover:!text-primary-50 p-3 md:p-5 md:py-4 py-5 text-white rounded-full w-full'}`}
          >
            <MdAddShoppingCart className={'text-base'} />Add To Cart
          </Button>
        </div>
      </div>
      <ProductDetailInfo data={data} />
      {showLoginModal && (
        <LoginScreen onClose={() => setShowLoginModal(false)} />
      )}
    </div>
  );
};

ProductInformation.propTypes = {
  data: PropTypes.object.isRequired,
  selectedColor: PropTypes.string,
  setSelectedColor: PropTypes.func.isRequired,
};

export default ProductInformation;
